/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import * as yup from 'yup';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Container,
  Link,
  OutlinedInput,
  InputAdornment,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Error } from '@material-ui/icons';

interface EnterpriseLoginBoxProps {
  setEnableEnterpriseLogin: Dispatch<SetStateAction<boolean>>;
  handleEnterpriseLoginClick: (userEmail: string) => void;
}

const useStyles = makeStyles((theme) => ({
  enterpriseLoginText: {
    marginTop: theme.spacing(1),
    '& .MuiInputBase-root': {
      height: theme.spacing(5),
    },
    '& .MuiOutlinedInput-input': {
      padding: '11.5px 14px',
    },
  },
  loginTextAdornedEnd: {
    paddingRight: 0,
  },
  continueEnterpriseLoginBtn: {
    ...theme.custom.login.continueEnterpriseLoginBtn
  },
  enterpriseContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  goBackLink: {
    ...theme.custom.login.enterpriseGoBackLink
  },
}));

export default function EnterpriseLoginBox(props: EnterpriseLoginBoxProps) {
  const classes = useStyles();
  const intl = useIntl();
  const { setEnableEnterpriseLogin, handleEnterpriseLoginClick } = props;
  const [enterpriseUserEmail, setEnterpriseUserEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState<boolean>();
  const [displayError, setDisplayError] = useState(false);
  const [inputEmailError, setInputEmailError] = useState('');

  const emailValidationSchema = yup
    .string()
    .email(
      intl.formatMessage({
        id: 'elements.enterpriseLoginBox.authentication.login.textfield.invalidEmail',
        defaultMessage:
          'Enter a valid email address in the format email@example.com',
      })
    )
    .required(
      intl.formatMessage({
        id: 'elements.enterpriseLoginBox.authentication.login.textfield.emptyEmail',
        defaultMessage: 'Enter the email address',
      })
    );

  const validateEnterpriseEmail = (email: string) => {
    try {
      emailValidationSchema.validateSync(email, { abortEarly: false });
      setIsEmailValid(true);
      setInputEmailError('');
    } catch (emailError: any) {
      setInputEmailError(emailError.message);
      setIsEmailValid(false);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const userEmail = event.target.value;
    setDisplayError(false);
    validateEnterpriseEmail(userEmail);
    setEnterpriseUserEmail(userEmail);
  };

  const handleContinueClick = () => {
    if (isEmailValid) {
      handleEnterpriseLoginClick(enterpriseUserEmail);
    } else {
      validateEnterpriseEmail(enterpriseUserEmail);
      setDisplayError(true);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleContinueClick();
    }
  };

  return (
    <Box>
      <Container className={classes.enterpriseContainer}>
        <OutlinedInput
          autoFocus
          id="outlined-basic"
          className={classes.enterpriseLoginText}
          classes={{ adornedEnd: classes.loginTextAdornedEnd }}
          placeholder={intl.formatMessage({
            id: 'authentication.login.textfield.enterpriseEmail',
            defaultMessage: 'Enter email',
          })}
          value={enterpriseUserEmail}
          onChange={handleEmailChange}
          onKeyPress={handleKeyPress}
          error={displayError}
          endAdornment={
            displayError && (
              <InputAdornment position="start">
                <Tooltip
                  data-testid="enterprise-email-tooltip"
                  title={inputEmailError}
                  arrow
                >
                  <Error style={{ color: 'red' }} />
                </Tooltip>
              </InputAdornment>
            )
          }
        />
        <Button
          variant="contained"
          className={classes.continueEnterpriseLoginBtn}
          onClick={handleContinueClick}
        >
          <FormattedMessage
            id="authentication.login.button.enterpriseContinue"
            defaultMessage="Continue"
          />
        </Button>
        <Link
          className={classes.goBackLink}
          component="button"
          variant="body2"
          onClick={() => {
            setEnableEnterpriseLogin(false);
          }}
        >
          <FormattedMessage
            id="authentication.login.button.enterpriseBack"
            defaultMessage="Back"
          />
        </Link>
      </Container>
    </Box>
  );
}
